import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContextMySQL';
import { authHeaders, API_URL } from '../api';

const ChatContext = createContext();

// Connexion Socket.io
const socket = io(API_URL, {
  autoConnect: false,
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionAttempts: 5,
});

export function ChatProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);

  // Connexion Socket.io
  useEffect(() => {
    if (isAuthenticated && user) {
      socket.auth = { token: user.token };
      socket.connect();

      socket.on('connect', () => {
        console.log('Connecté au serveur Socket.io');
        setIsConnected(true);
      });

      socket.on('disconnect', () => {
        console.log('Déconnecté du serveur Socket.io');
        setIsConnected(false);
      });

      socket.on('new_message', (message) => {
        if (message.conversation_id === currentConversationId) {
          setMessages(prev => [...prev, message]);
        }
        // Mettre à jour le compteur de non lus
        updateUnreadCount();
      });

      socket.on('new_notification', (notification) => {
        console.log('Nouvelle notification:', notification);
        updateUnreadCount();
        // Afficher une notification toast ici
      });

      socket.on('unread_count', ({ count }) => {
        setUnreadCount(count);
      });

      socket.on('conversation_history', ({ conversationId, messages: historyMessages }) => {
        if (conversationId === currentConversationId) {
          setMessages(historyMessages);
        }
      });

      socket.on('conversation_created', ({ conversationId }) => {
        loadConversation(conversationId);
      });

      socket.on('error', (error) => {
        console.error('Erreur Socket.io:', error);
      });
    }

    return () => {
      socket.disconnect();
    };
  }, [isAuthenticated, user]);

  const updateUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_URL}/api/conversations/${user.id}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        const totalUnread = data.conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
        setUnreadCount(totalUnread);
      }
    } catch (error) {
      console.error('Erreur mise à jour compteur:', error);
    }
  }, [user]);

  const loadConversations = useCallback(async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_URL}/api/conversations/${user.id}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
    }
  }, [user]);

  const loadConversation = useCallback(async (conversationId) => {
    setCurrentConversationId(conversationId);
    try {
      const response = await fetch(`${API_URL}/api/conversations/${conversationId}/messages`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages);
        socket.emit('join_conversation', { conversationId });
      }
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    }
  }, []);

  const createSupportConversation = useCallback((orderId) => {
    if (!isAuthenticated || !user) return null;
    
    socket.emit('create_support_conversation', { orderId });
    
    return null;
  }, [isAuthenticated, user]);

  const sendMessage = useCallback((text, conversationId = currentConversationId) => {
    if (!text.trim() || !conversationId) return;

    socket.emit('send_message', {
      conversationId,
      text,
      type: 'text'
    });
  }, [currentConversationId]);

  const markAsRead = useCallback((conversationId = currentConversationId) => {
    if (!conversationId) return;
    
    socket.emit('mark_as_read', { conversationId });
    
    // Mettre à jour les messages localement
    setMessages(prev => 
      prev.map(msg => ({
        ...msg,
        is_read: true
      }))
    );
  }, [currentConversationId]);

  const toggleChat = useCallback(async () => {
    if (!isOpen && isAuthenticated && user) {
      await loadConversations();
      if (conversations.length === 0) {
        // Créer une nouvelle conversation de support si aucune n'existe
        createSupportConversation('default');
      } else {
        // Charger la première conversation
        loadConversation(conversations[0].id);
      }
    }
    setIsOpen(prev => !prev);
  }, [isOpen, isAuthenticated, user, conversations, loadConversations, createSupportConversation, loadConversation]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <ChatContext.Provider value={{
      conversations,
      activeConversation,
      messages,
      isOpen,
      unreadCount,
      isConnected,
      currentConversationId,
      createSupportConversation,
      sendMessage,
      markAsRead,
      toggleChat,
      closeChat,
      loadConversation,
      loadConversations,
      setIsOpen,
      setCurrentConversationId,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
}
